import React from 'react';
import { Heart, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import References from '../components/References';

const BalancedLifestyle = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1477332552946-cfb384aeaf1c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxoZWFsdGglMjB3ZWxsbmVzc3xlbnwwfHx8fDE3NzA3NjE1NjZ8MA&ixlib=rb-4.1.0&q=85)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-teal-900/80 to-teal-700/80" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Keeping a Balanced Lifestyle</h1>
          <p className="text-xl">Moderate drinking can fit into a healthy, balanced life</p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 py-16">
        {/* Introduction */}
        <div className="mb-12">
          <p className="text-lg text-gray-700 leading-relaxed">
            Maintaining a healthy lifestyle is about balance - and moderate drinking can fit into a balanced lifestyle. Drinking within recommended guidelines can help keep the health risks from alcohol low.
          </p>
        </div>

        {/* Guidelines Card */}
        <Card className="mb-12 bg-gradient-to-br from-teal-50 to-cyan-50 border-teal-200">
          <CardHeader>
            <CardTitle className="flex items-center text-2xl">
              <Heart className="mr-3 h-8 w-8 text-teal-600" />
              Recommended Drinking Guidelines
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <Alert className="bg-teal-100 border-teal-300">
              <AlertCircle className="h-5 w-5 text-teal-700" />
              <AlertDescription className="text-teal-900 font-semibold">
                Plan to have at least two alcohol-free days every week
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold text-pink-700 mb-4">For Women</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="mr-2 h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
                    <span><strong>2</strong> standard drinks a day</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="mr-2 h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
                    <span>No more than <strong>10</strong> standard drinks a week</span>
                  </li>
                  <li className="flex items-start">
                    <AlertCircle className="mr-2 h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <span>No more than <strong>4</strong> standard drinks on any single occasion</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold text-blue-700 mb-4">For Men</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="mr-2 h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
                    <span><strong>3</strong> standard drinks a day</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="mr-2 h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
                    <span>No more than <strong>15</strong> standard drinks a week</span>
                  </li>
                  <li className="flex items-start">
                    <AlertCircle className="mr-2 h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <span>No more than <strong>5</strong> standard drinks on any single occasion</span>
                  </li>
                </ul>
              </div>
            </div>

            <p className="text-gray-700 text-center italic">
              Keeping to these levels makes for a better night out – and a better day after. It reduces the likelihood of suffering a hangover or unpleasant experiences.
            </p>
          </CardContent>
        </Card>

        {/* When Not to Drink */}
        <Card className="mb-12">
          <CardHeader>
            <CardTitle className="flex items-center text-2xl">
              <XCircle className="mr-3 h-8 w-8 text-red-600" />
              When Not to Drink
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">Avoid alcohol if you:</p>
            <ul className="space-y-3">
              <li className="flex items-start">
                <XCircle className="mr-3 h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Could be pregnant, are pregnant or trying to get pregnant</span>
              </li>
              <li className="flex items-start">
                <XCircle className="mr-3 h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Are on medication that interacts with alcohol</span>
              </li>
              <li className="flex items-start">
                <XCircle className="mr-3 h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Have a condition made worse by drinking alcohol</span>
              </li>
              <li className="flex items-start">
                <XCircle className="mr-3 h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Feel unwell, depressed, tired or cold as alcohol could make things worse</span>
              </li>
              <li className="flex items-start">
                <XCircle className="mr-3 h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Are about to operate machinery or a vehicle or do anything that is risky or requires skill</span>
              </li>
              <li className="flex items-start">
                <XCircle className="mr-3 h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Are under 18 - the safest option is to delay drinking for as long as possible</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* References Section */}
        <References />
      </div>
    </div>
  );
};

export default BalancedLifestyle;